import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, Truck, FileText, Mail, Phone, ArrowLeft, Loader2 } from "lucide-react";
import { API, ASSETS } from "@/lib/site";

const TOKEN_KEY = "trey_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("quotes");

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setData(null);
  }, []);

  const fetchData = useCallback(async (tk) => {
    try {
      const res = await fetch(`${API}/admin/submissions`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      if (res.status === 401) {
        logout();
        toast.error("Session expired. Please sign in again.");
        return;
      }
      if (!res.ok) throw new Error();
      setData(await res.json());
    } catch {
      toast.error("Failed to load submissions.");
    }
  }, [logout]);

  useEffect(() => {
    if (token) fetchData(token);
  }, [token, fetchData]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) throw new Error();
      const json = await res.json();
      localStorage.setItem(TOKEN_KEY, json.access_token);
      setToken(json.access_token);
      toast.success("Welcome back.");
    } catch {
      toast.error("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center px-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md border border-white/10 bg-surface p-8 md:p-10"
        >
          <img src={ASSETS.logo} alt="Trey's Trucking" className="mb-8 h-16 w-auto object-contain" />
          <h1 className="font-display text-2xl font-bold uppercase tracking-tight">Admin Sign In</h1>
          <p className="mt-2 text-sm text-[#a1a1a8]">View incoming quotes and driver applications.</p>
          <form onSubmit={login} data-testid="admin-login-form" className="mt-8 flex flex-col gap-5">
            <label className="flex flex-col gap-2">
              <span className="text-xs uppercase tracking-widest text-[#a1a1a8]">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email"
                className="w-full rounded-none border-0 border-b border-white/20 bg-transparent py-3 text-white outline-none focus:border-brand"
                placeholder="admin@treystruckinglogistics.com"
              />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-xs uppercase tracking-widest text-[#a1a1a8]">Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password"
                className="w-full rounded-none border-0 border-b border-white/20 bg-transparent py-3 text-white outline-none focus:border-brand"
                placeholder="••••••••"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="mt-2 flex items-center justify-center gap-2 bg-brand px-8 py-4 text-sm font-semibold uppercase tracking-wide text-ink transition-colors hover:bg-white disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </button>
            <a href="/" className="mt-2 flex items-center gap-2 text-xs uppercase tracking-widest text-[#71717a] hover:text-white">
              <ArrowLeft className="h-3 w-3" /> Back to site
            </a>
          </form>
        </motion.div>
      </div>
    );
  }

  const quotes = data?.quotes || [];
  const drivers = data?.drivers || [];
  const list = tab === "quotes" ? quotes : drivers;

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-white/10 bg-ink/85 px-5 py-4 backdrop-blur-xl md:px-10">
        <div className="flex items-center gap-4">
          <img src={ASSETS.logo} alt="Trey's Trucking" className="h-11 w-auto object-contain" />
          <span className="hidden text-xs uppercase tracking-[0.3em] text-[#a1a1a8] sm:block">Submissions Inbox</span>
        </div>
        <div className="flex items-center gap-3">
          <a href="/" className="text-xs uppercase tracking-widest text-[#a1a1a8] hover:text-white">View site</a>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="flex items-center gap-2 border border-white/25 px-4 py-2 text-xs uppercase tracking-wide transition-colors hover:border-brand hover:text-brand"
          >
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-5 py-10 md:px-10">
        <div className="mb-8 flex gap-3">
          <button
            onClick={() => setTab("quotes")}
            data-testid="admin-tab-quotes"
            className={`flex items-center gap-2 border px-5 py-3 text-sm uppercase tracking-wide transition-colors ${
              tab === "quotes" ? "border-brand bg-brand text-ink" : "border-white/15 text-[#a1a1a8] hover:text-white"
            }`}
          >
            <FileText className="h-4 w-4" /> Quotes ({quotes.length})
          </button>
          <button
            onClick={() => setTab("drivers")}
            data-testid="admin-tab-drivers"
            className={`flex items-center gap-2 border px-5 py-3 text-sm uppercase tracking-wide transition-colors ${
              tab === "drivers" ? "border-brand bg-brand text-ink" : "border-white/15 text-[#a1a1a8] hover:text-white"
            }`}
          >
            <Truck className="h-4 w-4" /> Drivers ({drivers.length})
          </button>
        </div>

        {!data ? (
          <div className="flex items-center gap-3 py-20 text-[#a1a1a8]">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading submissions...
          </div>
        ) : list.length === 0 ? (
          <div className="border border-dashed border-white/15 py-20 text-center text-[#71717a]">
            No {tab === "quotes" ? "quote requests" : "driver applications"} yet.
          </div>
        ) : (
          <div data-testid={tab === "quotes" ? "admin-quotes-list" : "admin-drivers-list"} className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {list.map((item) => (
              <div key={item.id} className="border border-white/10 bg-surface p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl font-bold uppercase tracking-tight">{item.name}</h3>
                    {tab === "quotes" && item.company ? (
                      <span className="text-sm text-[#a1a1a8]">{item.company}</span>
                    ) : null}
                  </div>
                  <span className="text-xs uppercase tracking-widest text-[#71717a]">
                    {new Date(item.created_at).toLocaleString()}
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  <a href={`mailto:${item.email}`} className="flex items-center gap-2 text-[#d4d4d8] hover:text-brand">
                    <Mail className="h-4 w-4 text-brand" /> {item.email}
                  </a>
                  <a href={`tel:${item.phone}`} className="flex items-center gap-2 text-[#d4d4d8] hover:text-brand">
                    <Phone className="h-4 w-4 text-brand" /> {item.phone}
                  </a>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 border-t border-white/10 pt-4 text-sm">
                  {tab === "quotes" ? (
                    <>
                      <Detail label="Freight" value={item.freight_type} />
                      <Detail label="Lane" value={`${item.origin || "—"} → ${item.destination || "—"}`} />
                      <Detail label="Details" value={item.message} full />
                    </>
                  ) : (
                    <>
                      <Detail label="CDL Class" value={item.cdl_class} />
                      <Detail label="Experience" value={item.experience} />
                      <Detail label="Message" value={item.message} full />
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Detail({ label, value, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-xs uppercase tracking-widest text-[#71717a]">{label}</div>
      <div className="mt-1 text-[#e4e4e7]">{value || "—"}</div>
    </div>
  );
}
